import React from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {DateRange} from 'rsuite/DateRangePicker';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import InputText from 'shared/ui/Input/InputText';
import Select from 'shared/ui/Select/Select';
import DateRangePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DateRangePickerControlled';
import OrderByControlled from 'shared/ui/OrderBy/OrderByControlled';
import {TagsFilterField} from 'entities/monitor';
import IOrderBy from 'shared/model/IOrderBy';
import {booleanToString, stringsToDateRange} from 'shared/lib/filterHelper';
import {useSort} from 'shared/lib/hooks/useSort';
import {
    DOMAIN_MONITORS_DEFAULT_SORT,
    DOMAIN_MONITORS_ORDER_FIELDS,
    getDomainMonitorsFilters,
} from 'features/domain/lib/domainFilters';
import {isTranslate} from 'shared/lib/lang';

import styles from './DomainMonitorFiltersForm.module.scss';

interface IDomainMonitorFiltersFormProps {
    onSubmit: SubmitHandler<IDomainMonitorFiltersForm>;
    button: React.ReactElement;
}

export interface IDomainMonitorFiltersForm {
    domain?: string,
    tags?: string[],
    active?: string,
    sslRedirect?: string,
    rknBlock?: string,
    sslActiveTo?: DateRange,
    domainActiveTo?: DateRange,
    orderBy?: IOrderBy,
}

const DomainMonitorFiltersForm: React.FC<IDomainMonitorFiltersFormProps> = ({onSubmit, button}) => {
    const [searchParams] = useSearchParams();
    const filters = getDomainMonitorsFilters(searchParams);
    const [sort] = useSort({defaultSort: DOMAIN_MONITORS_DEFAULT_SORT, sortFields: DOMAIN_MONITORS_ORDER_FIELDS});

    const {register, handleSubmit, control} = useForm<IDomainMonitorFiltersForm>({
        mode: 'onSubmit',
        defaultValues: {
            active: booleanToString(filters.active),
            sslRedirect: booleanToString(filters.ssl_redirect),
            rknBlock: booleanToString(filters.rkn_block),
            tags: filters.tags ?? [],
        },
    });

    const activeOptions = [
        {label: `${t`Активен`}`, value: 'true'},
        {label: `${t`На паузе`}`, value: 'false'},
    ];

    const yesNoOptions = [
        {label: `${t`Есть`}`, value: 'true'},
        {label: `${t`Нет`}`, value: 'false'},
    ];

    const sortOptions = [
        {label: `${t`Домен`}`, value: 'domain'},
        {label: `${t`Состояние`}`, value: 'active'},
        {label: `${t`SSL годен до`}`, value: 'ssl_active_to'},
        {label: `${t`Домен зарегистрирован до`}`, value: 'domain_active_to'},
        {label: `${t`Редирект на https`}`, value: 'ssl_redirect'},
        ...(!isTranslate() ? [{label: `${t`Блокировка РКН`}`, value: 'rkn_block'}] : []),
    ];

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError label={t`Домен или его часть`}>
                    <InputText
                        register={register}
                        defaultValue={filters.domain}
                        name="domain"
                        placeholder=""
                        control={control}
                    />
                </FormGroupWithError>

                <TagsFilterField control={control}/>

                <FormGroupWithError label={t`Состояние`}>
                    <Select
                        control={control}
                        name="active"
                        data={activeOptions}
                        defaultValue={booleanToString(filters.active)}
                        cleanable={true}
                        searchable={false}
                    />
                </FormGroupWithError>

                <FormGroupWithError label={t`Редирект на HTTPS`}>
                    <Select
                        control={control}
                        name="sslRedirect"
                        data={yesNoOptions}
                        defaultValue={booleanToString(filters.ssl_redirect)}
                        cleanable={true}
                        searchable={false}
                    />
                </FormGroupWithError>

                {!isTranslate() && (
                    <FormGroupWithError label={t`Блокировка РКН`}>
                        <Select
                            control={control}
                            name="rknBlock"
                            data={yesNoOptions}
                            defaultValue={booleanToString(filters.rkn_block)}
                            cleanable={true}
                            searchable={false}
                        />
                    </FormGroupWithError>
                )}

                <FormGroupWithError
                    label={t`Диапазон дат, в который попадает окончание срока годности SSL сертификата`}
                >
                    <DateRangePickerControlled
                        control={control}
                        name="sslActiveTo"
                        defaultValue={stringsToDateRange(
                            filters['ssl_active_to[after]'],
                            filters['ssl_active_to[before]'],
                        )}
                        cleanable={true}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    label={t`Диапазон дат, в который попадает окончание срока регистрации домена`}
                >
                    <DateRangePickerControlled
                        control={control}
                        name="domainActiveTo"
                        defaultValue={stringsToDateRange(
                            filters['domain_active_to[after]'],
                            filters['domain_active_to[before]'],
                        )}
                        cleanable={true}
                    />
                </FormGroupWithError>

                <FormGroupWithError label={t`Сортировка`}>
                    <OrderByControlled
                        control={control}
                        name="orderBy"
                        defaultValue={sort}
                        data={sortOptions}
                    />
                </FormGroupWithError>
            </ModalBodyWrapper>
            <FormGroup>
                {button}
            </FormGroup>
        </form>
    );
};

export default DomainMonitorFiltersForm;
